import {
  computeToolCallStatus,
  ToolCallAccordionItem,
} from "src/base-components/agents/ToolCallDisplay";
import { ChatLoadingIndicator } from "src/base-components/Chat/ChatLoadingIndicator";
import { ChatLoadingSpinner } from "src/base-components/Chat/ChatLoadingSpinner";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { ChatUserBubble } from "src/base-components/Chat/ChatUserBubble";
import {
  type SessionMessagesResponseMessagesInner,
  type ToolMessage,
  ToolType,
} from "src/clients/agent-management";
import type { LabelDictionaries } from "src/flow/analytics/m1/VisualizeMetrics/specialDomainScaling";
import { VisualizeMetricsToolMessage } from "src/flow/analytics/m1/VisualizeMetrics/VisualizeMetricsToolMessage";

const renderHumanContent = (content: unknown): string => {
  if (typeof content === "string") return content;
  return JSON.stringify(content);
};

const buildToolResultMap = (
  messages: SessionMessagesResponseMessagesInner[],
): Map<string, ToolMessage> => {
  const entries = messages
    .filter((m): m is { type: "tool" } & ToolMessage => m.type === "tool")
    .map((m) => [m.tool_call_id, m] as const);
  return new Map(entries);
};

export const LiveAnalyticsMessages: React.FC<{
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  isProvisioning: boolean;
  failureMessage: string | null;
  labelDictionaries: LabelDictionaries;
}> = ({
  messages,
  isActive,
  isProvisioning,
  failureMessage,
  labelDictionaries,
}) => {
  const toolResults = buildToolResultMap(messages);

  const sessionSubstatus = isActive && !isProvisioning ? "running" : null;

  const renderableMessages = messages.filter(
    (m) => m.type === "human" || m.type === "ai",
  );

  return (
    <>
      {renderableMessages.map((message, idx) => {
        const key = message.id ?? `${message.type}-${idx}`;
        if (message.type === "human") {
          return (
            <ChatMessage key={key} dataLoc="copilot-message-user" role="user">
              <ChatUserBubble message={renderHumanContent(message.content)} />
            </ChatMessage>
          );
        }
        // AI message
        return (
          <ChatMessage key={key} dataLoc="copilot-message-ai" role="assistant">
            <div className="flex flex-col gap-2">
              {(message.content_blocks ?? []).map((block, blockIdx) => {
                if (block.type === "text") {
                  return (
                    <ChatTextContent
                      key={`${key}-text-${blockIdx}`}
                      content={block.text}
                      delay={0}
                      useMarkdownRenderer
                    />
                  );
                }
                // Skip the structured-output tool (json_response) — it's the
                // agent's final answer, not a user-facing tool call.
                if (block.metadata.type === ToolType.STRUCTURED_OUTPUT) {
                  return null;
                }
                const result = toolResults.get(block.id) ?? null;
                if (block.name === "reply_visualize_metrics") {
                  return (
                    <VisualizeMetricsToolMessage
                      key={`${key}-tool-${block.id}`}
                      labelDictionaries={labelDictionaries}
                      toolCall={block}
                      toolResult={result}
                    />
                  );
                }
                return (
                  <ToolCallAccordionItem
                    key={`${key}-tool-${block.id}`}
                    showDescription={false}
                    showTokenUsage={false}
                    status={computeToolCallStatus(result, sessionSubstatus)}
                    toolCall={block}
                    toolResult={result}
                  />
                );
              })}
            </div>
          </ChatMessage>
        );
      })}
      {isActive &&
        (isProvisioning ? (
          <ChatLoadingSpinner
            dataLoc="copilot-provisioning"
            text="Setting up agent..."
          />
        ) : (
          <ChatLoadingIndicator mode="loading" dataLoc="copilot-thinking" />
        ))}
      {failureMessage && (
        <li
          className="font-inter-normal-12px text-red-600"
          data-loc="copilot-error"
        >
          {failureMessage}
        </li>
      )}
    </>
  );
};
